import React, { useState, useEffect } from "react";
import Header from "../../components/ui/Header";
import KPICard from "./components/KPICard";
import SalesChart from "./components/SalesChart";
import TopPerformersCard from "./components/TopPerformersCard";
import CollectionAlertsCard from "./components/CollectionAlertsCard";
import SalesVsCollectionChart from "./components/SalesVsCollectionChart";
import FilterControls from "./components/FilterControls";
import Icon from "../../components/AppIcon";
import { supabase } from "utils/supabase";
import { format } from "date-fns";
import EntriesUpdate from "./components/EntriesUpdate";

const ExecutiveSalesOverviewDashboard = () => {
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [kpiData, setKpiData] = useState([]);
  const [topPerformersData, setTopPerformersData] = useState([]);
  const [salesChartData, setSalesChartData] = useState([]);

  const [companyOptions, setCompanyOptions] = useState([
    { value: "all", label: "All Companies" },
  ]);

  const [allEntries, setAllEntries] = useState([]);

  useEffect(() => {
    const fetchEntriesForUpdate = async () => {
      let query = supabase.from("sales_entries").select("*");

      // Apply filter logic
      const today = new Date();
      let startDate, endDate;

      switch (selectedFilters.dateRange) {
        case "today":
          startDate = endDate = today.toISOString().split("T")[0];
          break;
        case "week":
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay());
          startDate = startOfWeek.toISOString().split("T")[0];
          endDate = today.toISOString().split("T")[0];
          break;
        case "month":
          const startOfMonth = new Date(
            today.getFullYear(),
            today.getMonth(),
            1
          );
          startDate = startOfMonth.toISOString().split("T")[0];
          endDate = today.toISOString().split("T")[0];
          break;
        case "custom":
          startDate = selectedFilters.customStartDate;
          endDate = selectedFilters.customEndDate;
          break;
        default:
          break;
      }

      if (startDate && endDate) {
        query = query.gte("entry_date", startDate).lte("entry_date", endDate);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Failed to fetch entries for update:", error);
      } else {
        setAllEntries(data);
      }
    };

    fetchEntriesForUpdate();
  }, [selectedFilters]);

  useEffect(() => {
    const fetchCompanies = async () => {
      const { data, error } = await supabase
        .from("companies")
        .select("id, name, code")
        .order("name", { ascending: true });

      if (error) {
        console.error("Error fetching companies:", error);
        return;
      }

      const options = data.map((company) => ({
        value: company.id,
        label: `${company.name} (${company.code})`,
      }));

      setCompanyOptions([{ value: "all", label: "All Companies" }, ...options]);
    };

    fetchCompanies();
  }, []);

  useEffect(() => {
    const fetchKPIs = async () => {
      setIsLoading(true);

      let query = supabase
        .from("sales_entries")
        .select(
          "sales_amount, collection_amount, outstanding_amount, collection_rate"
        );

      // ✅ Apply company filter
      if (
        selectedFilters?.companies &&
        selectedFilters.companies.length > 0 &&
        !selectedFilters.companies.includes("all")
      ) {
        query = query.in("company_id", selectedFilters.companies);
      }

      const today = new Date();
      let startDate = null;
      let endDate = null;

      switch (selectedFilters.dateRange) {
        case "today":
          startDate = endDate = today.toISOString().split("T")[0];
          break;
        case "week":
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay());
          startDate = startOfWeek.toISOString().split("T")[0];
          endDate = today.toISOString().split("T")[0];
          break;
        case "month":
          const startOfMonth = new Date(
            today.getFullYear(),
            today.getMonth(),
            1
          );
          startDate = startOfMonth.toISOString().split("T")[0];
          endDate = today.toISOString().split("T")[0];
          break;
        case "custom":
          startDate = selectedFilters.customStartDate;
          endDate = selectedFilters.customEndDate;
          break;
        default:
          break;
      }

      if (startDate && endDate) {
        query = query.gte("entry_date", startDate).lte("entry_date", endDate);
      }

      const { data: entries, error } = await query;

      if (error) {
        console.error("Error fetching sales entries:", error);
        setIsLoading(false);
        return;
      }

      let totalSales = 0;
      let totalCollection = 0;
      let totalOutstanding = 0;
      let collectionRates = [];

      console.log("entries", entries);

      entries.forEach((e) => {
        totalSales += e.sales_amount || 0;
        totalCollection += e.collection_amount || 0;
        totalOutstanding += e.outstanding_amount || 0;
        if (e.collection_rate != null)
          collectionRates.push(parseFloat(e.collection_rate) || 0);
      });

      const averageCollectionRate =
        totalSales > 0 ? ((totalCollection / totalSales) * 100).toFixed(1) : 0;

      const outStandingAmount = Math.max(0, totalSales - totalCollection);

      setKpiData([
        {
          title: "Total Sales",
          value: totalSales,
          change: 0,
          changeType: "increase",
          icon: "BadgeIndianRupee",
          color: "primary",
        },
        {
          title: "Collections",
          value: totalCollection,
          change: 0,
          changeType: "increase",
          icon: "TrendingUp",
          color: "success",
        },
        {
          title: "Outstanding Amount",
          value: outStandingAmount,
          change: 0,
          changeType: "decrease",
          icon: "AlertCircle",
          color: "warning",
        },
        {
          title: "Collection Rate",
          value: `${averageCollectionRate}%`,
          change: 0,
          changeType: "increase",
          icon: "Target",
          color: "success",
        },
      ]);

      setIsLoading(false);
    };

    fetchKPIs();
  }, [selectedFilters]);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const { data, error } = await supabase
          .from("sales_entries")
          .select("entry_date, sales_amount, collection_amount")
          .order("entry_date", { ascending: true });

        if (error) throw error;

        // Group by date and sum sales and collections
        const grouped = {};

        data.forEach((entry) => {
          const dateKey = entry.entry_date;
          if (!grouped[dateKey]) {
            grouped[dateKey] = {
              date: dateKey,
              sales: 0,
              collection: 0,
            };
          }
          grouped[dateKey].sales += entry.sales_amount || 0;
          grouped[dateKey].collection += entry.collection_amount || 0;
        });

        // Convert object to array and calculate collectionRate
        const transformedData = Object.values(grouped).map((item) => {
          const { date, sales, collection } = item;
          const collectionRate =
            sales > 0 ? Math.round((collection / sales) * 100) : 0;

          return { date, sales, collectionRate };
        });

        setSalesChartData(transformedData);
      } catch (err) {
        console.error("Failed to load chart data:", err.message);
      }
    };

    fetchChartData();
  }, []);

  // Mock top performers data
  useEffect(() => {
    const fetchTopPerformers = async () => {
      const { data, error } = await supabase
        .from("sales_entries")
        .select(
          "company_id, sales_amount, collection_amount, collection_rate, companies ( name )"
        );

      if (error) {
        console.error("Error fetching top performers:", error);
        return;
      }

      const companyMap = {};

      data.forEach((entry) => {
        const id = entry.company_id;
        if (!companyMap[id]) {
          companyMap[id] = {
            id,
            name: entry.companies.name,
            sales: 0,
            collections: 0,
            collectionRateSum: 0,
            count: 0,
          };
        }
        companyMap[id].sales += entry.sales_amount || 0;
        companyMap[id].collections += entry.collection_amount || 0;
        companyMap[id].collectionRateSum += entry.collection_rate || 0;
        companyMap[id].count += 1;
      });

      const result = Object.values(companyMap).map((company) => ({
        id: company.id,
        name: company.name,
        sales: company.sales,
        collections: company.collections,
        collectionRate: ((company.collections / company.sales) * 100).toFixed(
          1
        ),
        // collectionRate: (company.collectionRateSum / company.count).toFixed(1),
      }));

      // Sort top 5 by sales
      result.sort((a, b) => b.sales - a.sales);

      // setTopPerformersData(result.slice(0, 5));

      setTopPerformersData(result);
    };

    fetchTopPerformers();
  }, []);

  const [collectionAlertsData, setCollectionAlerts] = useState([]);

  useEffect(() => {
    const fetchCollectionAlerts = async () => {
      const now = new Date();

      // 1. Fetch companies
      const { data: companies, error: companyErr } = await supabase
        .from("companies")
        .select("id, name");

      if (companyErr) {
        console.error("Error fetching companies:", companyErr);
        return;
      }

      const companyMap = {};
      companies.forEach((c) => {
        companyMap[c.id] = c.name;
      });

      // 2. Fetch sales entries
      const { data: entries, error: salesErr } = await supabase
        .from("sales_entries")
        .select(
          "id, company_id, collection_amount, collection_rate, outstanding_amount, created_at"
        );

      if (salesErr) {
        console.error("Error fetching sales entries:", salesErr);
        return;
      }

      const alerts = [];

      // 3. Group by company_id
      const grouped = {};
      entries.forEach((entry) => {
        const id = entry.company_id;
        if (!grouped[id]) grouped[id] = [];
        grouped[id].push(entry);
      });

      Object.entries(grouped).forEach(([companyId, records]) => {
        const company = companyMap[companyId] || "Unknown Company";

        const recent = records.filter((e) => {
          const diffDays =
            (now - new Date(e.created_at)) / (1000 * 60 * 60 * 24);
          return diffDays <= 3;
        });

        // 🔹 Zero-collection alert
        const allZero =
          recent.length >= 3 &&
          recent.every((r) => (r.collection_amount || 0) === 0);
        if (allZero) {
          alerts.push({
            id: crypto.randomUUID(),
            company,
            type: "zero-collection",
            message: "No collections recorded for 3 consecutive days",
            amount: recent.reduce(
              (sum, r) => sum + (r.collection_amount || 0),
              0
            ),
            timestamp: new Date(recent.at(-1)?.created_at || now),
          });
        }

        // 🔹 Low-collection alert
        const low = records.find((r) => (r.collection_rate || 0) < 50);
        if (low) {
          alerts.push({
            id: crypto.randomUUID(),
            company,
            type: "low-collection",
            message: "Collection rate below 50% threshold",
            amount: low.collection_amount || 0,
            timestamp: new Date(low.created_at),
          });
        }

        // 🔹 Overdue alert
        const overdue = records.find((r) => {
          const age = (now - new Date(r.created_at)) / (1000 * 60 * 60 * 24);
          return r.outstanding_amount > 0 && age > 30;
        });
        if (overdue) {
          alerts.push({
            id: crypto.randomUUID(),
            company,
            type: "overdue",
            message: "Outstanding amount exceeds 30-day limit",
            amount: overdue.outstanding_amount,
            timestamp: new Date(overdue.created_at),
          });
        }
      });

      setCollectionAlerts(alerts);
    };

    fetchCollectionAlerts();
  }, []);

  const [salesVsCollectionData, setSalesVsCollectionData] = useState([]);

  useEffect(() => {
    const fetchSalesVsCollectionData = async () => {
      // 1. Fetch companies
      const { data: companies, error: companyErr } = await supabase
        .from("companies")
        .select("id, name");

      if (companyErr) {
        console.error("Error fetching companies:", companyErr);
        return;
      }

      // 2. Fetch sales_entries
      const { data: entries, error: salesErr } = await supabase
        .from("sales_entries")
        .select("company_id, sales_amount, collection_amount");

      if (salesErr) {
        console.error("Error fetching sales entries:", salesErr);
        return;
      }

      // 3. Group by company_id
      const grouped = {};
      entries.forEach((entry) => {
        const id = entry.company_id;
        if (!grouped[id]) {
          grouped[id] = { sales: 0, collections: 0 };
        }
        grouped[id].sales += entry.sales_amount || 0;
        grouped[id].collections += entry.collection_amount || 0;
      });

      // 4. Build final array
      const finalData = Object.entries(grouped).map(([companyId, totals]) => {
        const company = companies.find((c) => c.id === companyId);
        return {
          company: company?.name || "Unknown Company",
          sales: totals.sales,
          collections: totals.collections,
        };
      });

      // 5. Optional: sort by sales descending
      finalData.sort((a, b) => b.sales - a.sales);

      setSalesVsCollectionData(finalData);
    };

    fetchSalesVsCollectionData();
  }, []);

  // Auto-refresh functionality
  useEffect(() => {
    let intervalId;

    if (autoRefreshEnabled) {
      intervalId = setInterval(() => {
        handleDataRefresh();
      }, refreshInterval * 60 * 1000); // Convert minutes to milliseconds
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefreshEnabled, refreshInterval]);

  // Manual refresh event listener
  useEffect(() => {
    const handleManualRefresh = () => {
      handleDataRefresh();
    };

    window.addEventListener("manualRefresh", handleManualRefresh);
    return () =>
      window.removeEventListener("manualRefresh", handleManualRefresh);
  }, []);

  const handleDataRefresh = async () => {
    setIsLoading(true);

    // Simulate API call delay
    setTimeout(() => {
      setLastUpdated(new Date());
      setIsLoading(false);
    }, 1000);
  };

  const handleFiltersChange = (filters) => {
    setSelectedFilters(filters);
    // In a real app, this would trigger data refetch with new filters
  };

  const handleRefreshToggle = (enabled, interval) => {
    setAutoRefreshEnabled(enabled);
    setRefreshInterval(interval);
  };

  const handleChartDrillDown = (data) => {
    // console.log("Drill down data:", data);
    // In a real app, this would navigate to detailed view or show modal
  };

  const formatLastUpdated = () => {
    return lastUpdated.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="px-4 lg:px-6 py-6 pt-36 sm:pt-20">
        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Executive Sales Overview Dashboard
            </h1>
            <p className="text-muted-foreground">
              Comprehensive daily sales performance insights across all
              companies
            </p>
          </div>

          <div className="flex items-center space-x-4 mt-4 lg:mt-0">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Clock" size={16} />
              <span>Last updated: {formatLastUpdated()}</span>
              {isLoading && (
                <Icon
                  name="RefreshCw"
                  size={16}
                  className="animate-spin text-primary"
                />
              )}
            </div>
          </div>
        </div>

        {/* Filter Controls */}
        <FilterControls
          companyOptions={companyOptions}
          onFiltersChange={handleFiltersChange}
          onRefreshToggle={handleRefreshToggle}
        />

        {/* KPI Cards Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {kpiData.map((kpi, index) => (
            <KPICard
              key={index}
              title={kpi.title}
              value={kpi.value}
              change={kpi.change}
              changeType={kpi.changeType}
              icon={kpi.icon}
              color={kpi.color}
            />
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
          {/* Sales Chart - 8 columns */}
          <div className="lg:col-span-8">
            <SalesChart
              data={salesChartData}
              onDrillDown={handleChartDrillDown}
            />
            <EntriesUpdate
              companyOptions={companyOptions}
              allEntries={allEntries}
              filterType={selectedFilters.dateRange} // or "day" or "month"
            />
          </div>

          {/* Right Sidebar - 4 columns */}
          <div className="lg:col-span-4 space-y-6">
            <TopPerformersCard companies={topPerformersData} />
            <CollectionAlertsCard alerts={collectionAlertsData} />
          </div>
        </div>

        {/* Sales vs Collection Gap Analysis */}
        <div className="mb-8">
          <SalesVsCollectionChart data={salesVsCollectionData} />
        </div>

        {/* Footer Info */}
        <div className="text-center text-sm text-muted-foreground">
          <p>
            Dashboard automatically refreshes every {refreshInterval} minutes
            when auto-refresh is enabled. Data reflects real-time sales
            performance across all registered companies.
          </p>
        </div>
      </main>
    </div>
  );
};

export default ExecutiveSalesOverviewDashboard;
